import { Link } from "react-router-dom";
import type { DashboardSiteActivity } from "@crewledger/shared/schemas";
import { Building2, MapPin } from "lucide-react";
import { EmptyState, StatusBadge } from "@/components/design";
import { formatHours, formatPercent } from "../utils";

interface SiteActivityStripProps {
  sites: DashboardSiteActivity[];
  loading: boolean;
}

export function SiteActivityStrip({ sites, loading }: SiteActivityStripProps) {
  if (loading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-40 min-w-[260px] animate-pulse rounded-xl border bg-card-white"
          />
        ))}
      </div>
    );
  }

  if (sites.length === 0) {
    return (
      <div className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Site activity
        </h2>
        <EmptyState
          icon={Building2}
          title="No active sites"
          description="Add a job site to start tracking labor, equipment, and pay by location."
          actionLabel="View sites"
          onAction={() => {
            window.location.assign("/sites");
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Site activity
        </h2>
        <Link to="/sites" className="text-xs font-medium text-orange-construction hover:underline">
          All sites
        </Link>
      </div>
      <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2 snap-x snap-mandatory">
        {sites.map((site) => (
          <article
            key={site.siteId}
            className="min-w-[260px] flex-1 snap-start rounded-xl border border-navy-deep/10 bg-card-white p-4 shadow-[var(--shadow-card)] sm:min-w-[280px]"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-mono text-xs font-bold uppercase tracking-widest text-orange-construction">
                  {site.siteCode}
                </p>
                <h3 className="mt-0.5 font-semibold text-navy-deep">{site.siteName}</h3>
              </div>
              <StatusBadge status={site.status} showDot={false} />
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">Total hours</dt>
                <dd className="font-semibold tabular-nums text-navy-deep">
                  {formatHours(site.totalHours)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">OT share</dt>
                <dd className="font-semibold tabular-nums text-navy-deep">
                  {formatPercent(site.overtimePercent)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Workers today</dt>
                <dd className="font-semibold tabular-nums text-navy-deep">
                  {site.activeWorkers}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Equipment</dt>
                <dd className="font-semibold tabular-nums text-navy-deep">
                  {site.equipmentInUse}
                </dd>
              </div>
            </dl>

            <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              Active job site
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
